import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useReturn } from "util/return";
import { profileSlice } from "redux/features/profile";
import { Tabs, Button, Box, Content } from "react-bulma-components";
import { FaCheck, FaHandPointRight, FaArrowRight } from "react-icons/fa";

import Icon from "util/icon";
import IconButton from "util/iconButton";

const { Tab } = Tabs;

const STEP_ACTIVE = 1;
const STEP_COMPLETE = 2;

function initializeRegistrationProgress(registrationPending, itemStatus) {
  if (!registrationPending) return null;
  const incompleteAffiliation =
    registrationPending.require_demographics &&
    itemStatus.affiliation.incomplete;
  const incompleteDemographics =
    registrationPending.require_demographics &&
    itemStatus.demographics.incomplete &&
    Array.from(Object.values(itemStatus.demographics.incomplete)).includes(
      true
    );

  const steps = [];
  if (incompleteAffiliation || incompleteDemographics) {
    steps.push({
      label: incompleteAffiliation ? "Affiliation" : "Review Affiliation",
      checker: (newItemStatus) =>
        newItemStatus.affiliation.reviewed &&
        !newItemStatus.affiliation.incomplete
          ? STEP_COMPLETE
          : STEP_ACTIVE,
      // This message will only be shown if affiliation was initially complete.
      // If it was initially incomplete, dashboard will automatically load the
      // affiliation-editing page.
      dashboardMessage:
        "Please review your affiliation information below, " +
        "and update if necessary",
      actions: () => [profileSlice.actions.setAffiliationReviewed(true)],
    });

    steps.push({
      label: incompleteDemographics
        ? "Demographic Background"
        : "Review Other Personal Information",
      checker: (newItemStatus) =>
        newItemStatus.demographics.reviewed
          ? // Currently the questions on the demographics page are not
            // mandatory and it is possible for somebody to "Save Changes"
            // without actually putting in any information. Temporarily we
            //  will allow this, andconsider the "demographics data entry"
            // step to be complete as long as "reviewed" is set.

            //  &&
            //    Object.values(newItemStatus.demographics.incomplete || {})
            // .includes(true)

            STEP_COMPLETE
          : newItemStatus.affiliation.reviewed &&
            !newItemStatus.affiliation.incomplete
          ? STEP_ACTIVE
          : null,
      // This message will only be shown if demographics were initially
      // complete (but affiliation was not). If demographics were initially
      // incomplete, dashboard will auotmatically load the demographics-
      // editing page.
      dashboardMessage:
        "Please review the rest of your personal " +
        "information below, and update if necessary",
      actions: () => [profileSlice.actions.setDemographicsReviewed(true)],
    });
  } else {
    steps.push({
      label: "Review Personal Information",
      checker: (newItemStatus) =>
        newItemStatus.affiliation.reviewed &&
        newItemStatus.demographics.reviewed
          ? STEP_COMPLETE
          : STEP_ACTIVE,
      dashboardMessage:
        "Please review your personal information below, " +
        "and update if necessary",
      actions: () => [
        profileSlice.actions.setAffiliationReviewed(true),
        profileSlice.actions.setDemographicsReviewed(true),
      ],
    });
  }

  steps.push({
    label: "Finalize Registration",
    checker: (newItemStatus) =>
      newItemStatus.affiliation.reviewed && newItemStatus.demographics.reviewed
        ? STEP_ACTIVE
        : null,
  });

  return steps;
}

export function RegistrationProgress({ forDashboard, forActivity }) {
  const {
    registrationPending,
    registrationProgress,
    itemStatus,
    returnOnCancel,
  } = useSelector(profileSlice.selector);

  const dispatch = useDispatch();
  const initializeTo =
    registrationPending &&
    itemStatus.affiliation.incomplete !== null &&
    itemStatus.demographics.incomplete !== null &&
    !registrationProgress
      ? initializeRegistrationProgress(registrationPending, itemStatus)
      : null;

  useEffect(() => {
    if (initializeTo)
      dispatch(profileSlice.actions.setRegistrationProgress(initializeTo));
  }, [initializeTo, dispatch]);

  const showingActivityId = registrationPending?.id;

  useEffect(() => {
    if (forActivity && forActivity.id !== showingActivityId)
      dispatch(
        profileSlice.actions.setRegistrationPending({
          id: forActivity.id,
          title: forActivity.title,
          require_demographics: forActivity.require_demographics,
        })
      );
  }, [forActivity, showingActivityId, dispatch]);

  const doReturn = useReturn();

  if (!registrationProgress) return null;
  const cancelRegistration = () => {
    dispatch(profileSlice.actions.setRegistrationPending(null));
    if (returnOnCancel) doReturn();
  };

  const stepStatus = registrationProgress.map(
    ({ label, checker, dashboardMessage, actions }) => {
      const check = itemStatus === null ? null : checker(itemStatus);
      return {
        label,
        isActive: check === STEP_ACTIVE,
        isComplete: check === STEP_COMPLETE,
        message:
          forDashboard && check === STEP_ACTIVE ? dashboardMessage : null,
        actions,
      };
    }
  );

  const [dashboardDisplay] = stepStatus.filter(({ message }) => !!message);
  return (
    <Box>
      <div className="is-italic block">
        Registration in Progress: {registrationPending.title}
        <Button text onClick={cancelRegistration}>
          Cancel registration
        </Button>
      </div>{" "}
      <Tabs disabled className="is-success" type="toggle-rounded" fullwidth>
        {" "}
        {stepStatus.map(({ label, isActive, isComplete }, i) => {
          return (
            <Tab color="success" key={i} active={isActive || isComplete}>
              {isActive ? (
                <Icon icon={FaHandPointRight} />
              ) : isComplete ? (
                <Icon icon={FaCheck} />
              ) : null}{" "}
              {i + 1}. {label}
            </Tab>
          );
        })}
      </Tabs>
      {dashboardDisplay ? (
        <Content align="center">
          {dashboardDisplay.message}, then press "Continue with Registration".
          <div className="buttons is-centered">
            <IconButton
              icon={FaArrowRight}
              onClick={() =>
                dashboardDisplay.actions().forEach((action) => dispatch(action))
              }
            >
              Continue with Registration
            </IconButton>
          </div>
        </Content>
      ) : null}
    </Box>
  );
}

export default RegistrationProgress;
